import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

import * as LoginSignupEvents from 'tradera/legacy/static/app/ui/layout/login-signup-events';
import * as loginAnalytics from 'tradera/legacy/static/app/ui/layout/login-signup-analytics';
import Analytics from 'packages/analytics';
import {
    useCurrentLanguage,
    useTranslator,
    useUrlLocalizer
} from 'tradera/lang/translate';
import { loadScript } from 'tradera/utils/script';
import { getLoginLocaleFromLanguage } from './get-login-locale-from-language';
import { FacebookButton } from 'tradera/components/social-buttons/social-buttons';

const FacebookLoginButton = (props) => {
    const {
        facebookAppId,
        compact,
        disabled,
        isModal,
        onError,
        tabIndex,
        className
    } = props;
    const [isLoading, setIsLoading] = useState(false);
    const { t } = useTranslator();
    const { toLocalizedUrl } = useUrlLocalizer();
    const { language } = useCurrentLanguage();
    const locale = getLoginLocaleFromLanguage(language);

    // Pre-loading & init needed on load to prevent popup blocking in Safari
    useEffect(() => {
        (async () => {
            await loadScript(
                `//connect.facebook.net/${locale || 'sv_SE'}/sdk.js`,
                {
                    timeout: 5000
                }
            );
            window.FB.init({
                appId: facebookAppId,
                version: 'v2.9'
            });
        })();
    }, [facebookAppId, locale]);

    const performLogin = async (loginResponse) => {
        try {
            const response = await axios.post(
                '/login/facebook/verify' + window.location.search,
                {
                    accessToken: loginResponse.authResponse.accessToken,
                    userId: loginResponse.authResponse.userID
                }
            );

            if (isModal) {
                Analytics.trackEvent({
                    category: 'Top navigation',
                    action: 'Login menu',
                    label: 'Button - Facebook login'
                });
            }
            loginAnalytics.flow.set(loginAnalytics.FLOW.LOGIN);
            loginAnalytics.method.set(loginAnalytics.METHOD.FACEBOOK);
            loginAnalytics.trackEvent('user-login-submit');

            LoginSignupEvents.trackLoginSuccess(response.data.memberId);
            window.parent.location = response.data.returnUrl;
        } catch (error) {
            onError(error);
            LoginSignupEvents.trackLoginFailure('facebook-auth');
            window.parent.location = toLocalizedUrl('/login?connect=Facebook');
        }
    };

    const onClick = () => {
        if (disabled) {
            return;
        }

        setIsLoading(true);
        onError(null);
        try {
            window.FB.login(
                (response) => {
                    if (response.status === 'connected') {
                        performLogin(response);
                    } else {
                        return;
                    }
                },
                {
                    scope: 'public_profile,email'
                }
            );
        } catch (error) {
            onError(error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <FacebookButton
            onClick={onClick}
            disabled={disabled}
            tabIndex={tabIndex}
            loading={isLoading}
            round={compact}
            className={className}>
            {!compact && t('login_withProvider', { provider: 'Facebook' })}
        </FacebookButton>
    );
};

FacebookLoginButton.propTypes = {
    className: PropTypes.string,
    facebookAppId: PropTypes.string,
    isModal: PropTypes.bool,
    tabIndex: PropTypes.number
};

FacebookLoginButton.defaultProps = {
    className: '',
    tabIndex: 0
};

export default FacebookLoginButton;
